/**
 * Component: Simple Search widget surface
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R12
 *
 * Embeddable Simple Search surface. Wraps
 * ``POST /api/v1/search/simple`` (keyword search against the
 * indexed corpus). Ships a query input with the inline
 * Search button, the result list, and the shared inline
 * error banner. The Batch C "results per page" filter was
 * dropped — the portal Simple Search view doesn't expose it
 * either; the request defaults to a 10-row page.
 */

import React, { useCallback, useState } from 'react';

import type { WidgetApiClient } from '../api/client';
import { WidgetError } from '../api/errors';
import { trackWidgetUsage } from '../core/usage-tracking';
import { BrowseLink } from './browse-link';
import {
  SURFACE_PRIMARY_BUTTON_STYLE,
  SURFACE_RESULTS_LIST_STYLE,
  SURFACE_RESULT_CARD_STYLE,
  SURFACE_WRAPPER_STYLE,
  SearchResultExpander,
  SurfaceErrorBanner,
  explainWidgetError,
  normalizeQuotes,
  pickResultSubtitle,
  pickResultTitle,
} from './shared';
import { SearchFilterPanel } from './search-filter-panel';
import {
  type Facet,
  type SearchFilters,
  INITIAL_FILTERS,
  toFilterRequest,
} from './search-filter-types';
import { useFilterAutoSearch } from './use-filter-auto-search';

interface SimpleSearchResult {
  document_id: string;
  title: string;
  content: string;
  score: number;
  highlights?: Record<string, string[]>;
  metadata?: Record<string, unknown>;
  source_url?: string | null;
}

interface SimpleSearchResponse {
  results?: SimpleSearchResult[];
  total_count?: number | null;
  /** WIDGET-V1-R27: available index facets for the refine panel. */
  facets?: Facet[];
}

export interface SimpleSearchProps {
  client: WidgetApiClient;
  baseUrl: string;
  token: string;
  onError?: (error: Error) => void;
  enableBrowseLinks?: boolean;
}

type Phase = 'idle' | 'loading' | 'done' | 'error';

const SIMPLE_PAGE_LIMIT = 10;

const SIMPLE_INPUT_AUTOFILL_CSS = [
  '[data-galen-surface="simple-search"] input:-webkit-autofill,',
  '[data-galen-surface="simple-search"] input:-webkit-autofill:hover,',
  '[data-galen-surface="simple-search"] input:-webkit-autofill:focus,',
  '[data-galen-surface="simple-search"] input:-webkit-autofill:active{',
  '-webkit-box-shadow:0 0 0 1000px var(--galen-background,#ffffff) inset!important;',
  'box-shadow:0 0 0 1000px var(--galen-background,#ffffff) inset!important;',
  '-webkit-text-fill-color:var(--galen-foreground,#0f172a)!important;',
  'caret-color:var(--galen-foreground,#0f172a);',
  'transition:background-color 9999s ease-in-out 0s;}',
].join('');

function SearchGlyph(): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

export const SimpleSearch: React.FC<SimpleSearchProps> = ({
  client,
  onError,
  enableBrowseLinks = false,
}) => {
  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [response, setResponse] = useState<SimpleSearchResponse | null>(null);
  // WIDGET-V1-R27: server-side faceted filtering state.
  const [filters, setFilters] = useState<SearchFilters>(INITIAL_FILTERS);
  const [facets, setFacets] = useState<Facet[]>([]);
  // Bumped on a fresh query submit to collapse the refine panel.
  const [filterCollapseToken, setFilterCollapseToken] = useState(0);

  // WIDGET-V1-R27: re-runnable search sending the refine selections (incl. the
  // figures-and-tables scope) and storing the returned facets.
  const runSearch = useCallback(async () => {
    if (query.trim().length === 0) return;

    setPhase('loading');
    setErrorMessage(null);

    // WIDGET-V1-R25: fire search_query on submit.
    void trackWidgetUsage(client, 'simple-search', 'search_query', {
      query_text: query,
      mode: 'simple',
    });

    try {
      const result = await client.postJson<SimpleSearchResponse>(
        '/api/v1/search/simple',
        {
          query: normalizeQuotes(query),
          limit: SIMPLE_PAGE_LIMIT,
          ...toFilterRequest(filters),
          limit_to_figures_tables: filters.limitToFiguresAndTables,
          prioritize_recent: filters.prioritizeRecent,
        },
      );
      setResponse(result);
      setFacets(result.facets ?? []);
      setPhase('done');
    } catch (e) {
      const err = e instanceof Error ? e : new WidgetError(String(e));
      setErrorMessage(explainWidgetError(err));
      setPhase('error');
      onError?.(err);
    }
  }, [client, query, filters, onError]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (phase === 'loading') return;
      setFilterCollapseToken((n) => n + 1);
      void runSearch();
    },
    [phase, runSearch],
  );

  // WIDGET-V1-R27: debounced re-search when a refine selection changes.
  useFilterAutoSearch(filters, response !== null, () => void runSearch());

  const isBusy = phase === 'loading';
  const resultCount =
    typeof response?.total_count === 'number'
      ? response.total_count
      : (response?.results?.length ?? 0);

  return (
    <div data-galen-surface="simple-search" style={SURFACE_WRAPPER_STYLE}>
      <div data-galen-simple-search-header>
        <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700 }}>
          Simple Search
        </h1>
        <p
          style={{
            margin: '0.25rem 0 0',
            fontSize: '0.9rem',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          Keyword search across the indexed guideline corpus.
        </p>
      </div>

      {errorMessage !== null && <SurfaceErrorBanner message={errorMessage} />}

      <form
        onSubmit={(e) => void handleSubmit(e)}
        data-galen-simple-search-form
        style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}
      >
        <style>{SIMPLE_INPUT_AUTOFILL_CSS}</style>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0 0.6rem',
            border: '1px solid var(--galen-border, #e2e8f0)',
            borderRadius: 'var(--galen-border-radius, 0.5rem)',
            background: 'var(--galen-background, #ffffff)',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          <SearchGlyph />
          <input
            id="galen-simple-query"
            data-galen-simple-search-query
            type="search"
            aria-label="Simple Search query"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={isBusy}
            placeholder="Enter keywords…"
            style={{
              flex: 1,
              minWidth: 0,
              boxSizing: 'border-box',
              padding: '0.5rem 0',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--galen-foreground, #0f172a)',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              appearance: 'none',
              WebkitAppearance: 'none',
            }}
          />
        </div>
        <button
          type="submit"
          disabled={isBusy || query.trim().length === 0}
          data-galen-simple-search-submit
          style={{
            ...SURFACE_PRIMARY_BUTTON_STYLE(isBusy || query.trim().length === 0),
            marginTop: 0,
            flexShrink: 0,
          }}
        >
          {isBusy ? 'Searching…' : 'Search'}
        </button>
      </form>

      {/* WIDGET-V1-R27: faceted refine panel (self-hides when nothing to refine) */}
      <SearchFilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        onReset={() => setFilters(INITIAL_FILTERS)}
        facets={facets}
        resultCount={response !== null ? resultCount : undefined}
        showFiguresToggle
        collapseToken={filterCollapseToken}
      />

      {phase === 'idle' && (
        <div
          data-galen-simple-search-empty
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '2.5rem 1rem',
            textAlign: 'center',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          <span style={{ opacity: 0.7 }}>
            <svg
              viewBox="0 0 24 24"
              width="36"
              height="36"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </span>
          <p style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: 'var(--galen-foreground, #0f172a)' }}>
            Enter a query to search
          </p>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            Find guideline sections that mention the keywords you enter.
          </p>
        </div>
      )}

      {phase === 'done' && response && (
        <div data-galen-simple-search-results>
          {typeof response.total_count === 'number' && (
            <div
              style={{
                fontSize: '0.85rem',
                color: 'var(--galen-muted, #475569)',
                marginBottom: '0.25rem',
              }}
            >
              {response.total_count} matching document
              {response.total_count === 1 ? '' : 's'}.
            </div>
          )}
          <ul style={SURFACE_RESULTS_LIST_STYLE}>
            {(response.results ?? []).map((result, index) => {
              const title = pickResultTitle(result);
              const subtitle = pickResultSubtitle(result);
              const guidelineId =
                result.metadata?.['Guidelineid'] ??
                result.metadata?.['GuidelineId'] ??
                null;
              const guidelineSectionIdRaw =
                result.metadata?.['GuidelineSectionId'];
              const sectionId =
                guidelineSectionIdRaw != null &&
                guidelineSectionIdRaw !== 'null'
                  ? (guidelineSectionIdRaw as number | string)
                  : null;
              const guidelineName =
                (result.metadata?.['GuidelineName'] as string | undefined) ??
                undefined;
              const sectionName =
                (result.metadata?.['GuidelineSectionName'] as
                  | string
                  | undefined) ?? undefined;
              return (
                <li
                  key={result.document_id}
                  data-galen-search-result-card
                  style={SURFACE_RESULT_CARD_STYLE}
                >
                  <div style={{ fontWeight: 600 }}>
                    {result.source_url ? (
                      <a
                        href={result.source_url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {title}
                      </a>
                    ) : (
                      title
                    )}
                  </div>
                  {subtitle && (
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--galen-muted, #475569)',
                        marginTop: '0.125rem',
                      }}
                    >
                      {subtitle}
                    </div>
                  )}
                  <SearchResultExpander
                    result={result}
                    surfaceKey="simple-search"
                    client={client}
                    query={query}
                    footerRight={
                      <BrowseLink
                        enabled={enableBrowseLinks}
                        guidelineId={guidelineId as number | string | null}
                        sectionId={sectionId}
                        client={client}
                        surfaceKey="simple-search"
                        documentId={result.document_id}
                        rankingPosition={index + 1}
                        guidelineName={guidelineName}
                        sectionName={sectionName}
                        query={query}
                      />
                    }
                  />
                </li>
              );
            })}
          </ul>
          {(response.results ?? []).length === 0 && (
            <div style={{ fontSize: '0.9rem', color: 'var(--galen-muted, #475569)' }}>
              No results.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
